import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// One stacking rule for every popup (owner 2026-10-26: the Insight opened BEHIND the Style profit search panel).
// Each modal registers on mount; its z-index = BASE + STEP * (open order), so the most recently opened is always on top.
// Overlays render through a portal on document.body (no ancestor transform / overflow / stacking context can trap them).
// Escape handlers must check layer.isTop() so only the top-most layer closes. The shared MultiSelect listbox uses
// z-index 10050, above every modal layer (and the app's legacy 10000 modals).
const stack = [];
let seq = 0;
const BASE = 1000, STEP = 10;

export function useModalLayer(active = true) {
  const id = useRef(0);
  if (!id.current) id.current = ++seq;
  const [z, setZ] = useState(BASE + STEP);
  useLayoutEffect(() => {
    if (!active) return undefined;
    const mine = id.current;
    stack.push(mine);
    setZ(BASE + STEP * stack.length);
    return () => { const i = stack.indexOf(mine); if (i >= 0) stack.splice(i, 1); };
  }, [active]);
  const isTop = useCallback(() => stack[stack.length - 1] === id.current, []);
  return { z, isTop };
}

// Backdrop + portal. Click on the backdrop itself closes. Pass `style`/`className` to restyle the backdrop.
export function ModalOverlay({ layer, onClose, className = "img-modal-overlay", style, children }) {
  return createPortal(
    <div className={className} data-modal-layer={layer.z} style={{ ...style, zIndex: layer.z }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {children}
    </div>,
    document.body
  );
}
